import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, FolderTree, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { serviceCategoriesApi } from "@/lib/api";
import { SUGGESTED_CATEGORIES } from "@/lib/serviceConstants";

const inputCls = "w-full rounded-xl border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function CategoriesManage() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, name, parent_id, active}

  const load = useCallback(async () => {
    setLoading(true);
    try { setCats(await serviceCategoriesApi.listAll()); }
    catch { toast.error("Failed to load categories"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tops = cats.filter((c) => !c.parent_id).sort((a, b) => a.order - b.order);
  const subsOf = (id) => cats.filter((c) => c.parent_id === id).sort((a, b) => a.order - b.order);

  const save = async (data) => {
    try {
      if (data.id) await serviceCategoriesApi.update(data.id, data);
      else await serviceCategoriesApi.create(data);
      toast.success("Category saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const quickAdd = async (name) => {
    try { await serviceCategoriesApi.create({ name, order: tops.length }); toast.success(`Added "${name}"`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Subcategories will also be removed.`)) return;
    try { await serviceCategoriesApi.remove(c.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (c) => {
    try { await serviceCategoriesApi.update(c.id, { ...c, active: !c.active }); load(); }
    catch { toast.error("Update failed"); }
  };

  const move = async (list, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx], b = list[j];
    try {
      await Promise.all([
        serviceCategoriesApi.update(a.id, { ...a, order: b.order }),
        serviceCategoriesApi.update(b.id, { ...b, order: a.order }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const Row = ({ c, list, idx, isSub }) => (
    <div className={`flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 ${isSub ? "ml-6" : ""}`} data-testid={`cat-row-${c.id}`}>
      <div className="flex flex-col">
        <button onClick={() => move(list, idx, -1)} disabled={idx === 0} className="text-muted-foreground disabled:opacity-30 hover:text-foreground"><ArrowUp className="h-3.5 w-3.5" /></button>
        <button onClick={() => move(list, idx, 1)} disabled={idx === list.length - 1} className="text-muted-foreground disabled:opacity-30 hover:text-foreground"><ArrowDown className="h-3.5 w-3.5" /></button>
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{c.name} {!c.active && <span className="ml-1 rounded bg-zinc-200 px-1.5 py-0.5 text-xs text-zinc-600">hidden</span>}</p>
        {c.description && <p className="text-xs text-muted-foreground">{c.description}</p>}
      </div>
      <button onClick={() => toggleActive(c)} data-testid={`cat-toggle-${c.id}`} title={c.active ? "Hide" : "Show"} className="rounded-lg border border-border p-2 hover:bg-secondary">{c.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
      <button onClick={() => setEditing({ id: c.id, name: c.name, description: c.description || "", parent_id: c.parent_id || "", active: c.active, order: c.order })} data-testid={`cat-edit-${c.id}`} className="rounded-lg border border-border p-2 hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
      <button onClick={() => del(c)} data-testid={`cat-delete-${c.id}`} className="rounded-lg border border-border p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8">
        <button onClick={() => navigate("/admin/services")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to services</button>
        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow text-gold">Marketplace</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold">Service Categories</h1>
          </div>
          <button onClick={() => setEditing({ name: "", description: "", parent_id: "", active: true, order: tops.length })} data-testid="new-category-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> Add</button>
        </div>

        {loading ? (
          <div className="mt-8 space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-secondary/60" />)}</div>
        ) : (
          <>
            {tops.length === 0 && (
              <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-8 text-center">
                <FolderTree className="mx-auto h-8 w-8 text-gold" />
                <p className="mt-3 text-sm text-muted-foreground">Add categories to organise your services. Quick-add from suggestions below.</p>
              </div>
            )}
            <div className="mt-6 space-y-2">
              {tops.map((c, i) => (
                <div key={c.id} className="space-y-2">
                  <Row c={c} list={tops} idx={i} />
                  {subsOf(c.id).map((sc, j) => <Row key={sc.id} c={sc} list={subsOf(c.id)} idx={j} isSub />)}
                </div>
              ))}
            </div>

            <div className="mt-10">
              <p className="eyebrow mb-3 text-gold">Quick add suggested categories</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_CATEGORIES.filter((n) => !cats.some((c) => c.name === n)).map((n) => (
                  <button key={n} onClick={() => quickAdd(n)} data-testid={`quick-cat-${n}`} className="rounded-full border border-dashed border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:border-gold hover:text-foreground">+ {n}</button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {editing && <CategoryModal cats={tops} value={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function CategoryModal({ cats, value, onClose, onSave }) {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    await onSave(f);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="category-modal">
      <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-serif text-xl font-semibold">{f.id ? "Edit" : "New"} category</h3><button onClick={onClose} className="rounded-full p-1.5 hover:bg-secondary"><X className="h-5 w-5" /></button></div>
        <div className="mt-4 space-y-3">
          <input data-testid="cat-name-input" className={inputCls} placeholder="Category name" value={f.name} onChange={(e) => set("name", e.target.value)} />
          <input data-testid="cat-desc-input" className={inputCls} placeholder="Short description (optional)" value={f.description} onChange={(e) => set("description", e.target.value)} />
          <select data-testid="cat-parent-input" className={inputCls} value={f.parent_id} onChange={(e) => set("parent_id", e.target.value)}>
            <option value="">Top-level category</option>
            {cats.filter((c) => c.id !== f.id).map((c) => <option key={c.id} value={c.id}>Subcategory of: {c.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--gold))]" /> Visible on public site</label>
        </div>
        <button onClick={submit} disabled={busy} data-testid="cat-save-btn" className="mt-5 w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Save"}</button>
      </div>
    </div>
  );
}
